import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export interface UserClaim { type: string; value: string }
export interface User { name: string | null; claims: UserClaim[]; isOwner: boolean }

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  signIn: (returnUrl?: string, scheme?: string) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface BffUserDto { name?: string | null; claims?: { type: string; value: string }[]; isOwner?: boolean }

async function fetchUser(): Promise<User | null> {
  const resp = await fetch('/bff/user', { credentials: 'include' });
  if (resp.status === 401) return null;
  if (!resp.ok) throw new Error(`/bff/user failed: ${resp.status}`);
  const dto = (await resp.json()) as BffUserDto;
  return { name: dto.name ?? null, claims: dto.claims ?? [], isOwner: dto.isOwner ?? false };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setUser(await fetchUser());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signIn = useCallback((returnUrl = '/', scheme?: string) => {
    const qs = new URLSearchParams({ returnUrl });
    if (scheme) qs.set('scheme', scheme);
    window.location.href = `/bff/login?${qs.toString()}`;
  }, []);

  const signOut = useCallback(async () => {
    // Top-level navigation, not fetch — the BFF responds with a 302 chain
    // (cookie clear → IdP federated logout → /welcome) and a cross-origin
    // redirect to Microsoft can't be followed by an XHR.
    window.location.href = '/bff/logout';
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: user !== null,
    loading,
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
